import React, { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Loader2, BookMarked, X, Presentation as PresentationIcon } from "lucide-react";
import api from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { CoverSlide } from "@/components/slides/CoverSlide";
import { MaturitySlide } from "@/components/slides/MaturitySlide";
import { CostSlide } from "@/components/slides/CostSlide";
import { ReframeSlide } from "@/components/slides/ReframeSlide";
import { ContextSlide } from "@/components/slides/ContextSlide";
import { AgentSlide } from "@/components/slides/AgentSlide";
import { PlaybackSlide } from "@/components/slides/PlaybackSlide";
import { ProofSlide } from "@/components/slides/ProofSlide";
import { FitSlide } from "@/components/slides/FitSlide";
import { HowSlide } from "@/components/slides/HowSlide";
import { CloseSlide } from "@/components/slides/CloseSlide";
import { PricingSlide } from "@/components/slides/PricingSlide";
import { SecuritySlide } from "@/components/slides/SecuritySlide";
import { FitAppendixSlide } from "@/components/slides/FitAppendixSlide";
import { PlaceholderSlide } from "@/components/slides/PlaceholderSlide";
import { AppendixSlide } from "@/components/slides/AppendixSlide";

const EASE = [0.22, 1, 0.36, 1];

export const Deck = ({ onLogout }) => {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(false);
  const [view, setView] = useState("main"); // 'main' | 'appendix'
  const [index, setIndex] = useState(0);
  const [aIndex, setAIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [demoCall, setDemoCall] = useState(null); // { conversationId, phone, agentName }
  const [presenterOpen, setPresenterOpen] = useState(false);
  const reduce = useReducedMotion();
  const touchX = useRef(null);
  const chanRef = useRef(null);
  const presenterWinRef = useRef(null);
  const stateRef = useRef({ view: "main", index: 0, aIndex: 0 });
  const actionsRef = useRef({});

  useEffect(() => {
    api
      .get("/deck/manifest")
      .then((res) => setManifest(res.data))
      .catch(() => setError(true));
  }, []);

  const slides = manifest?.slides || [];
  const appendix = manifest?.appendix || [];
  const total = slides.length;

  const go = useCallback(
    (delta) => {
      setDir(delta);
      if (view === "main") {
        setIndex((i) => Math.min(Math.max(i + delta, 0), total - 1));
      } else {
        setAIndex((i) => Math.min(Math.max(i + delta, 0), appendix.length - 1));
      }
    },
    [view, total, appendix.length]
  );

  const openAppendix = useCallback(() => {
    setDir(1);
    setAIndex(0);
    setView("appendix");
  }, []);
  const closeAppendix = useCallback(() => {
    setDir(-1);
    setView("main");
  }, []);

  // Toggle the private Presenter View in a separate pop-out window (⌘/Ctrl + L).
  const togglePresenter = useCallback(() => {
    const w = presenterWinRef.current;
    if (w && !w.closed) {
      w.close();
      presenterWinRef.current = null;
      setPresenterOpen(false);
      return;
    }
    presenterWinRef.current = window.open(
      "/presenter",
      "lexafide-presenter",
      "width=1280,height=820,menubar=no,toolbar=no,location=no"
    );
  }, []);

  // Keep latest state + actions available to the cross-window channel handler.
  stateRef.current = { view, index, aIndex };
  actionsRef.current = { go, openAppendix, closeAppendix };

  // Cross-window sync with the Presenter View.
  useEffect(() => {
    const ch = new BroadcastChannel("lexafide-deck");
    chanRef.current = ch;
    ch.onmessage = (ev) => {
      const m = ev.data || {};
      if (m.type === "nav") {
        actionsRef.current.go(m.delta);
      } else if (m.type === "toggle-appendix") {
        if (stateRef.current.view === "appendix") actionsRef.current.closeAppendix();
        else actionsRef.current.openAppendix();
      } else if (m.type === "request-state") {
        setPresenterOpen(true);
        ch.postMessage({ type: "state", ...stateRef.current });
      } else if (m.type === "presenter-bye") {
        setPresenterOpen(false);
      }
    };
    return () => ch.close();
  }, []);

  // Fallback: detect when the presenter pop-out is closed directly.
  useEffect(() => {
    const t = setInterval(() => {
      const w = presenterWinRef.current;
      if (w && w.closed) {
        presenterWinRef.current = null;
        setPresenterOpen(false);
      }
    }, 1500);
    return () => clearInterval(t);
  }, []);

  // Broadcast current position whenever it changes.
  useEffect(() => {
    chanRef.current?.postMessage({ type: "state", view, index, aIndex });
  }, [view, index, aIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "l" || e.key === "L")) {
        e.preventDefault();
        togglePresenter();
        return;
      }
      const t = e.target;
      const tag = t?.tagName;
      const isField =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t?.isContentEditable;
      if (isField) return; // let form controls (sliders, number fields) handle keys
      const roleAttr = t?.getAttribute?.("role");
      const isTab = roleAttr === "tab";
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        if (isTab) return; // milestone/agent tabs handle their own arrows
        e.preventDefault();
        go(1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        if (isTab) return;
        e.preventDefault();
        go(-1);
      } else if (e.key === " ") {
        if (tag === "BUTTON" || roleAttr === "button" || roleAttr === "tab") return;
        e.preventDefault();
        go(1);
      } else if (e.key === "Escape" && view === "appendix") {
        closeAppendix();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, view, closeAppendix, togglePresenter]);

  if (error) {
    return (
      <div className="stage-wrap">
        <div className="text-center">
          <p className="font-display text-2xl text-ink mb-2">
            Your session has expired.
          </p>
          <button
            data-testid="deck-relogin-button"
            onClick={onLogout}
            className="font-mono text-xs uppercase tracking-[0.24em] text-accent underline underline-offset-4"
          >
            Re-enter password
          </button>
        </div>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="stage-wrap">
        <Loader2 className="w-6 h-6 animate-spin text-ink-soft" />
      </div>
    );
  }

  const isAppendix = view === "appendix";
  const activeKey = isAppendix ? `a-${aIndex}` : `m-${index}`;
  const atStart = isAppendix ? aIndex === 0 : index === 0;
  const atEnd = isAppendix
    ? aIndex === appendix.length - 1
    : index === total - 1;

  const variants = {
    enter: (d) => ({ opacity: 0, x: reduce ? 0 : d * 48 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: reduce ? 0 : d * -48 }),
  };

  const onTouchStart = (e) => (touchX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div
      className="relative h-full w-full"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Scroll fades so content never touches the fixed chrome (mobile + short laptops) */}
      <div
        aria-hidden
        className="deck-fade absolute top-0 inset-x-0 h-16 bg-gradient-to-b from-[#F7F6F5] via-[#F7F6F5]/85 to-transparent z-10 pointer-events-none"
      />
      <div
        aria-hidden
        className="deck-fade absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[#F7F6F5] via-[#F7F6F5]/90 to-transparent z-10 pointer-events-none"
      />

      {/* Top chrome */}
      <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-[clamp(20px,4vw,56px)] py-[clamp(14px,2.6vh,26px)] pointer-events-none">
        <div className="pointer-events-auto">
          <Wordmark size="sm" />
        </div>
        <div className="pointer-events-auto flex items-center gap-4">
          {presenterOpen ? (
            <button
              data-testid="presenter-open-indicator"
              onClick={togglePresenter}
              title="Presenter notes window is open — press ⌘L to close"
              className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:bg-accent/15 transition-colors"
            >
              <PresentationIcon className="w-3 h-3" />
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
              Notes open
            </button>
          ) : null}
          {isAppendix ? (
            <button
              data-testid="appendix-close-button"
              onClick={closeAppendix}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-ink transition-colors"
            >
              <X className="w-3.5 h-3.5" /> Back to deck
            </button>
          ) : (
            <button
              data-testid="appendix-open-button"
              onClick={openAppendix}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-ink transition-colors"
            >
              <BookMarked className="w-3.5 h-3.5" /> Appendix
            </button>
          )}
        </div>
      </div>

      {/* Slide area */}
      <div className="h-full w-full overflow-hidden">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={activeKey}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: reduce ? 0.001 : 0.5, ease: EASE }}
            className="relative h-full w-full"
          >
            {isAppendix ? (
              aIndex === 0 ? (
                <PricingSlide />
              ) : aIndex === 1 ? (
                <SecuritySlide />
              ) : aIndex === 2 ? (
                <FitAppendixSlide />
              ) : (
                <AppendixSlide slide={appendix[aIndex]} />
              )
            ) : index === 0 ? (
              <CoverSlide data={manifest} />
            ) : index === 1 ? (
              <MaturitySlide slide={slides[1]} />
            ) : index === 2 ? (
              <CostSlide slide={slides[2]} />
            ) : index === 3 ? (
              <ReframeSlide slide={slides[3]} />
            ) : index === 4 ? (
              <ContextSlide slide={slides[4]} />
            ) : index === 5 ? (
              <AgentSlide slide={slides[5]} onDemoCall={setDemoCall} />
            ) : index === 6 ? (
              <PlaybackSlide slide={slides[6]} demoCall={demoCall} />
            ) : index === 7 ? (
              <ProofSlide slide={slides[7]} />
            ) : index === 8 ? (
              <FitSlide slide={slides[8]} />
            ) : index === 9 ? (
              <HowSlide slide={slides[9]} />
            ) : index === 10 ? (
              <CloseSlide slide={slides[10]} />
            ) : (
              <PlaceholderSlide slide={slides[index]} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom chrome: prev / counter / next */}
      <div className="absolute bottom-0 inset-x-0 z-20 flex items-center justify-between px-[clamp(20px,4vw,56px)] py-[clamp(14px,2.6vh,26px)]">
        <button
          data-testid="deck-prev-button"
          onClick={() => go(-1)}
          disabled={atStart}
          aria-label="Previous slide"
          className="group inline-flex items-center justify-center w-11 h-11 rounded-full border border-hairline text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-paper-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-ink disabled:hover:border-hairline"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div
          data-testid="deck-slide-counter"
          className="font-mono text-sm tracking-[0.28em] text-ink-soft tabular-nums"
        >
          {isAppendix ? (
            <span className="text-accent">
              APPENDIX {String(aIndex + 1).padStart(2, "0")} /{" "}
              {String(appendix.length).padStart(2, "0")}
            </span>
          ) : (
            <>
              <span className="text-ink">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="mx-1">/</span>
              <span>{String(total).padStart(2, "0")}</span>
            </>
          )}
        </div>

        <button
          data-testid="deck-next-button"
          onClick={() => go(1)}
          disabled={atEnd}
          aria-label="Next slide"
          className="group inline-flex items-center justify-center w-11 h-11 rounded-full border border-hairline text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-paper-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-ink disabled:hover:border-hairline"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Progress hairline */}
      {!isAppendix ? (
        <div className="absolute bottom-0 inset-x-0 h-px bg-hairline/60 z-10">
          <motion.div
            className="h-full bg-accent"
            initial={false}
            animate={{ width: `${((index + 1) / total) * 100}%` }}
            transition={{ duration: reduce ? 0.001 : 0.5, ease: EASE }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default Deck;
